/* Shopify commerce adapter for the standalone board designer. */
(function (scope) {
  class RefineDesignerCart {
    constructor({ root = '/', addons = {}, fetcher = (...args) => fetch(...args) } = {}) {
      this.root = root.endsWith('/') ? root : root + '/';
      this.addons = addons;
      this.fetcher = fetcher;
      this.products = new Map();
    }

    async product(handle, refresh = false) {
      if (refresh) this.products.delete(handle);
      if (!this.products.has(handle)) {
        const request = this.fetcher(this.root + 'products/' + encodeURIComponent(handle) + '.js', {
          headers: { Accept: 'application/json' }, cache: 'no-store'
        }).then(async response => {
          if (!response.ok) throw new Error('This board could not be loaded. Please try again.');
          const product = await response.json();
          if (!Array.isArray(product.variants)) throw new Error('This board could not be loaded. Please try again.');
          return product;
        }).catch(error => { this.products.delete(handle); throw error; });
        this.products.set(handle, request);
      }
      return this.products.get(handle);
    }

    async resolve(handle, sku, refresh = false) {
      const product = await this.product(handle, refresh);
      const matches = product.variants.filter(variant => variant.sku === sku);
      if (matches.length !== 1) throw new Error('This configuration is currently unavailable. Please choose another option.');
      return { product, variant: matches[0] };
    }

    addon(key) {
      const addon = this.addons[key];
      return addon?.available && Number(addon.id) > 0 && Number.isFinite(addon.price) ? addon : null;
    }

    form({ product, variant, quantity, feet, personalization }) {
      if (!variant?.available) throw new Error('This configuration is sold out. Please choose another option.');
      if (!Number.isInteger(quantity) || quantity < 1 || quantity > 99) throw new Error('Choose a quantity from 1 to 99.');
      const token = Date.now() + '-' + Math.random().toString(16).slice(2);
      const properties = { ...personalization.properties, 'Installed feet': feet === 'None' ? 'No feet' : 'Installed ' + feet.toLowerCase() + ' feet', '_Configuration token': token };
      const context = { '_Parent product': product.title, '_Parent SKU': variant.sku, '_Configuration token': token };
      const items = [{ id: variant.id, properties }];
      if (feet !== 'None') {
        const addon = this.addon(feet);
        if (!addon) throw new Error(feet + ' feet are temporarily unavailable. Choose No Feet to continue.');
        items.push({ id: addon.id, properties: { ...context } });
      }
      if (Number(properties['Additional engraving surfaces']) > 0) {
        const addon = this.addon('engraving');
        if (!addon || addon.price !== 2000) throw new Error('Second-surface engraving is temporarily unavailable. Please choose one surface.');
        items.push({ id: addon.id, properties: { ...context, '_Add-on kind': 'Engraving', 'Additional surface': 'Second engraved surface' } });
      }
      const form = new FormData();
      items.forEach((item, index) => {
        const prefix = 'items[' + index + ']';
        form.append(prefix + '[id]', item.id);
        form.append(prefix + '[quantity]', quantity);
        Object.entries(item.properties).forEach(([key, value]) => form.append(prefix + '[properties][' + key + ']', value));
      });
      personalization.files.forEach(({ name, file }) => form.append('items[0][properties][' + name + ']', file));
      return form;
    }

    async add(configuration) {
      const response = await this.fetcher(this.root + 'cart/add.js', {
        method: 'POST', headers: { Accept: 'application/json' }, body: this.form(configuration)
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || 'Unable to add your board. Please try again.');
      return result;
    }
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = RefineDesignerCart;
  else scope.RefineDesignerCart = RefineDesignerCart;
})(typeof window === 'undefined' ? globalThis : window);
